import * as actions from "./actions";
import { createMachine, send, TransitionsConfig } from "xstate";
import {
  FlowMachineContext,
  FlowEvents,
  FlowObjects,
  FlowMachineTypeState,
} from "./types";
import { OrderShift } from "../../model/Screens";

const addToStack = send<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, _: FlowEvents) => ({
    type: "ADD_UNDO_REDO",
    actions: ctx.sendAction,
  })
) as any;

const assertValidShift = (ctx: FlowMachineContext, event: FlowEvents) => {
  if (event.type === "REORDER_MMOS") {
    let maxLen = ctx.flow.screens[ctx.index].mixedMediaObjects.length;
    let oldIndex = ctx.flow.screens[ctx.index].mixedMediaObjects.findIndex(
      (mmo: FlowObjects) => mmo.id === event.id
    );
    if (event.direction === OrderShift.UP) {
      return maxLen >= oldIndex + 1;
    } else if (event.direction === OrderShift.DOWN) {
      return oldIndex - 1 >= 0;
    }
  }
  return true;
};

const logicEvents: TransitionsConfig<FlowMachineContext, FlowEvents> = {
  SET_INDEX: {
    // optional field, which state to switch to
    target: "idle",
    // Can be an array, string, function, functions as strings, i,e. "setIndex", are calling functions in actions.ts
    actions: [
      send({ type: "MARK_ALL_INACTIVE" }),
      "setIndex",
      "initializeRefs",
    ],
    cond: (ctx: FlowMachineContext, event: FlowEvents) =>
      ctx.flow.screens.length > 0 &&
      event.type == "SET_INDEX" &&
      event.index <= ctx.flow.screens.length - 1 &&
      event.index >= 0,
  },
  OVERRIDE_FLOW: { actions: "overrideFlow" },
  OVERRIDE_SCREEN: { actions: "overrideScreen" },
  SET_CURRENT_OBJECT: { actions: "setCurrentObject" },
  SET_INTERACTING: { actions: "setInteracting" },
  UNDO: {
    target: "undo_redo",
    actions: ["undo"],
    cond: (ctx: FlowMachineContext, _: FlowEvents) => ctx.undoStack.length > 0,
  },
  REDO: {
    target: "undo_redo",
    actions: "redo",
    cond: (ctx: FlowMachineContext, _: FlowEvents) => ctx.redoStack.length > 0,
  },
  OVERRIDE_SCREEN_PREVIEW: { actions: "overrideScreenPreview" },
  MARK_ALL_INACTIVE: {
    actions: ["markAllInactive", send({ type: "MARK_INACTIVE" })],
    cond: (ctx: FlowMachineContext, _: FlowEvents) => {
      return ctx.index >= 0;
    },
  },
  CHANGE_TITLE: {
    actions: [addToStack, "changeTitle"],
  },
  REORDER_SCREENS: {
    actions: [
      send({ type: "MARK_ALL_INACTIVE" }),
      addToStack,
      "reorderScreens",
      "reorderIndexChanger",
    ],
  },
  REORDER_MMOS: {
    actions: [addToStack, "reorderMmos"],
    cond: assertValidShift,
  },
  DELEGATE: {
    actions: ["delegate"],
  },
  SET_PARENT_SERVICE: { actions: "setParentService" },
  DELETE: { actions: [addToStack, "deleteObject"] },
  COMMIT: { actions: ["commit", addToStack] },
  ADD_MMO: { actions: [addToStack, "addMmo"] },
  ADD_BACKGROUND_MMO: { actions: [addToStack, "addBackgroundMmo"] },
  SET_COLOR: {
    actions: [addToStack, "setColor"],
    cond: (ctx: FlowMachineContext, _: FlowEvents) => !ctx.currentObject,
  },
  UPLOAD_DRAFTS: [
    {
      target: "complete",
      actions: ["updatePublishConfig"],
      cond: (ctx: FlowMachineContext, _: FlowEvents) => {
        console.info("skip upload drafts", ctx.drafts.length === 0);
        return ctx.drafts.length === 0;
      },
    },
    {
      actions: "uploadDrafts",
    },
  ],
  // When an event handling config is an array, and one or more objects in the array have a `cond`
  // key, the first object which the `cond` returns true is the only action that runs.
  UPDATE_URLS: [
    {
      target: "complete",
      actions: ["updateUrls", "clearDrafts"],
      cond: (ctx: FlowMachineContext, _: FlowEvents) => {
        return ctx.publishConfig.count >= ctx.drafts.length - 1;
      },
    },
    { actions: "updateUrls" },
  ],
  PUBLISH: {
    target: "complete",
    actions: ["clearDrafts", () => console.info("firing publish")],
  },
};

/**
 * We need to guarantee that we have all the necessary data to construct a {@link FlowMachineContext} before building our machine
 * @param initialContext all data to build a {@link FlowMachineContext}
 */
export const createFlowMachine = (initialContext: FlowMachineContext) => {
  return createMachine<FlowMachineContext, FlowEvents, FlowMachineTypeState>(
    {
      // Required unique ID
      id: "flow",
      // this means we have multiple 'states'
      type: "parallel",
      // Error if machine receives an EVENT it cannot handle
      // Context shared between all states of machine
      context: {
        ...initialContext,
      },
      strict: true,
      states: {
        logic: {
          // All states need an initial state
          initial: "idle",
          states: {
            // When this machine is entered run the following
            idle: {
              entry: "initializeRefs",
              always: "loaded",
            },
            loaded: {
              // Description of events to handle
              on: {
                ...logicEvents,
                ADD_UNDO_REDO: {
                  actions: ["addUndoRedo"],
                },
              },
            },
            undo_redo: {
              on: {
                ...logicEvents,
                ADD_UNDO_REDO: {
                  target: "loaded",
                },
                UNDO_CLEANUP: {
                  actions: ["undoCleanup"],
                  cond: (ctx: FlowMachineContext, _: FlowEvents) =>
                    ctx.undoStack.length > 0,
                },
                REDO_CLEANUP: {
                  actions: ["redoCleanup"],
                  cond: (ctx: FlowMachineContext, _: FlowEvents) =>
                    ctx.redoStack.length > 0,
                },
              },
            },
            complete: {
              // Invoke can be used to handle completion or error of a Promise, callback, or a child machine
              invoke: {
                id: "publishFlow",
                src: (ctx: FlowMachineContext, _: FlowEvents) =>
                  actions.publishFlow(ctx),
                onDone: {
                  target: "idle",
                  actions: "publishFlowCompletion",
                },
                onError: {
                  target: "error",
                  actions: actions.handlePublishError
                }
              },
            },
            error: {}
          },
        },
        textboxes: {
          initial: "inactive",
          strict: true,
          states: {
            inactive: {
              on: {
                MARK_ACTIVE: {
                  target: "active",
                  actions: ["textboxActive"],
                },
                MARK_EDITOR_ACTIVE: {
                  target: "active",
                  actions: ["textboxActive"],
                },
              },
            },
            active: {
              on: {
                MARK_INACTIVE: {
                  target: "inactive",
                  actions: "clearTextboxActive",
                },
              },
            },
          },
        },
      },
    },
    { actions }
  );
};
